import { Component, useState, useEffect } from 'react';
import LandingPage from './LandingPage';
import LoginPage from './LoginPage';
import ChurchApp from './ChurchApp';
import AdminDashboard, { AdminLogin } from './AdminDashboard';
import {
  isSupabaseConfigured,
  supabase,
  getMyProfile,
  getCachedProfile,
  isAdminSession,
  signOut,
} from './services/supabase';
import { withTimeout } from './lib/networkErrors';
import usageSync from './services/usageSync';

// How long to wait for the saved login to be restored before carrying on offline.
const SESSION_RESTORE_TIMEOUT_MS = 4000;

// A render error in the console must not blank the whole app mid-service;
// the display window is a separate window and survives a main-window crash.
class ErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('SleekHear crashed:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
          <div className="max-w-md text-center bg-slate-900 border border-red-500/30 rounded-2xl p-8">
            <h1 className="text-2xl font-bold text-red-400 mb-3">Something went wrong</h1>
            <p className="text-slate-300 text-sm mb-6">
              The console hit an unexpected error. Your display window (if open) is unaffected.
              Reloading will restore your saved libraries and settings.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-lg transition-all"
            >
              Reload SleekHear
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [showApp, setShowApp] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminAuthenticated, setAdminAuthenticated] = useState(false);
  const [userAuthenticated, setUserAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [restoringSession, setRestoringSession] = useState(isSupabaseConfigured);

  useEffect(() => {
    // Check for admin access via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('admin') === 'true') {
      setShowAdmin(true);
    }

    if (!isSupabaseConfigured) return;

    // Restore an existing Supabase session
    // With a saved but expired login and no connection, Supabase keeps retrying
    // the token refresh (holding its auth lock) for up to ~30 s. Don't leave the
    // page loading that long: after a few seconds carry on as if offline.
    withTimeout(supabase.auth.getSession(), SESSION_RESTORE_TIMEOUT_MS).then(async (result) => {
      const unavailable = !!(result.timedOut || result.error);
      const session = unavailable ? null : result.value.data.session;
      const sessionError = unavailable
        ? (result.error || new Error('Session lookup timed out'))
        : result.value.error;
      try {
        if (!session) {
          // A session error (e.g. offline with an expired token) is not a
          // sign-out: let a previously verified user keep working.
          const cached = sessionError ? getCachedProfile() : null;
          if (cached) {
            setUserAuthenticated(true);
            setCurrentUser(cached);
            setShowApp(true);
          }
          return;
        }
        if (isAdminSession(session)) {
          setAdminAuthenticated(true);
          return;
        }
        // Distinguish "offline / server error" (keep the session, use the
        // last verified profile) from "profile missing or inactive" (sign out).
        let profile;
        let lookupFailed = false;
        try {
          profile = await getMyProfile();
        } catch {
          lookupFailed = true;
          profile = getCachedProfile(session.user.id);
        }
        if (profile && profile.isActive) {
          setUserAuthenticated(true);
          setCurrentUser(profile);
          setShowApp(true);
        } else if (!lookupFailed) {
          await signOut();
        }
      } finally {
        setRestoringSession(false);
      }
    }).catch(() => setRestoringSession(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setUserAuthenticated(false);
        setCurrentUser(null);
        setAdminAuthenticated(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-lg text-center bg-slate-900 border border-red-500/30 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-red-400 mb-3">App Not Configured</h1>
          <p className="text-slate-300 text-sm">
            SleekHear cannot connect to its database. The environment variables{' '}
            <code className="text-amber-400">REACT_APP_SUPABASE_URL</code> and{' '}
            <code className="text-amber-400">REACT_APP_SUPABASE_ANON_KEY</code>{' '}
            must be set at build time. See the README for setup instructions.
          </p>
        </div>
      </div>
    );
  }

  if (restoringSession) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleLogout = async () => {
    setUserAuthenticated(false);
    setCurrentUser(null);
    setShowApp(false);
    setShowLogin(false);
    // Record the logout centrally while the session still exists.
    const sessionId = localStorage.getItem('currentSessionId');
    if (sessionId) {
      usageSync.enqueue('session', 'logout', {}, sessionId);
      await withTimeout(usageSync.flush(), 2000); // never hold up signing out
    }
    localStorage.removeItem('currentSessionId');
    await signOut();
  };

  // Admin dashboard access
  if (showAdmin) {
    if (!adminAuthenticated) {
      return <AdminLogin onLogin={() => setAdminAuthenticated(true)} />;
    }
    return <AdminDashboard onLogout={async () => {
      setAdminAuthenticated(false);
      setShowAdmin(false);
      await signOut();
      // Clear admin URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }} />;
  }

  // Main app - if authenticated, show the app
  if (userAuthenticated) {
    return (
      <ErrorBoundary>
        <ChurchApp user={currentUser} onLogout={handleLogout} />
      </ErrorBoundary>
    );
  }

  // If not authenticated but trying to access app, show login
  if (showLogin || showApp) {
    return <LoginPage onLogin={(user) => {
      setUserAuthenticated(true);
      setCurrentUser(user);
      setShowApp(true);
    }} />;
  }

  // Default: show landing page
  return <LandingPage onGetStarted={() => setShowLogin(true)} />;
}

export default App;
